import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { subscriptionService } from "../services/subscription.service";
import { hasPaidPlanAccess } from "../utils/basicPlanAccess";

const SubscriptionContext = createContext(null);

const getNormalizedPlanType = (subscription = {}) => {
  const planParts = [
    subscription?.plan_type,
    subscription?.planType,
    subscription?.plan_name,
    subscription?.subscription_plan,
    subscription?.plan,
    subscription?.plan_key,
    subscription?.price_plan,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (planParts.includes("pro")) return "pro";
  if (planParts.includes("basic")) return "basic";
  return "";
};

const emptySubscription = {
  has_access: false,
  subscription_status: "inactive",
  trial_end_date: null,
  plan_expiry_date: null,
  days_left: 0,
  has_used_trial: false,
  has_card_on_file: false,
  requires_payment_method: false,
};

export const SubscriptionProvider = ({ children }) => {
  const { token, user } = useSelector((state) => state.user);
  const [subscription, setSubscription] = useState(emptySubscription);
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createResponse, setCreateResponse] = useState(null);
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false);

  const status = subscription?.subscription_status;
  const hasEligibleStatus = status === "active" || status === "trialing";
  const requiresPaymentMethod = Boolean(subscription?.requires_payment_method);
  const hasPaidAccessFromUser = hasPaidPlanAccess(user);
  const normalizedPlanType = useMemo(
    () => getNormalizedPlanType(subscription) || getNormalizedPlanType(user),
    [subscription, user]
  );
  const isBasicPlan = normalizedPlanType === "basic";
  const isProPlan = normalizedPlanType === "pro";
  const hasAccess =
    Boolean(subscription?.has_access) ||
    (hasEligibleStatus && !requiresPaymentMethod) ||
    hasPaidAccessFromUser;

  const resetSubscriptionState = useCallback(() => {
    setSubscription(emptySubscription);
    setIsChecking(false);
    setCheckError("");
    setIsCreating(false);
    setCreateError("");
    setCreateResponse(null);
    setHasCheckedOnce(false);
  }, []);

  const checkSubscription = useCallback(async () => {
    if (!token) {
      resetSubscriptionState();
      return emptySubscription;
    }

    setIsChecking(true);
    setCheckError("");
    try {
      const data = await subscriptionService.checkSubscription(token);
      setSubscription({ ...emptySubscription, ...data });
      setHasCheckedOnce(true);
      return data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to check subscription status.";
      setCheckError(message);
      setHasCheckedOnce(true);
      throw error;
    } finally {
      setIsChecking(false);
    }
  }, [token, resetSubscriptionState]);

  const createSubscription = useCallback(
    async (planType, interval, extraPayload = {}) => {
      if (!token) throw new Error("Missing auth token");

      setIsCreating(true);
      setCreateError("");
      try {
        const createData = await subscriptionService.createSubscription(token, {
          plan_type: planType,
          interval,
          ...extraPayload,
        });
        setCreateResponse(createData);
        return createData;
      } catch (error) {
        const message =
          error?.response?.data?.message || "Failed to create subscription.";
        setCreateError(message);
        throw error;
      } finally {
        setIsCreating(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token) {
      resetSubscriptionState();
      return;
    }
    setSubscription(emptySubscription);
    setCreateResponse(null);
    setCreateError("");
    setCheckError("");
    setHasCheckedOnce(false);
    checkSubscription().catch(() => null);
  }, [token, checkSubscription, resetSubscriptionState]);

  const statusText = useMemo(() => {
    if (status === "trialing") {
      const daysLeft = Number(subscription?.days_left || 0);
      return `Trial active, ${daysLeft} days left`;
    }
    if (status === "active") {
      return "Subscription active";
    }
    if (hasPaidAccessFromUser) {
      return "Subscription active";
    }
    return "Subscription required";
  }, [hasPaidAccessFromUser, status, subscription]);

  const value = useMemo(
    () => ({
      subscription,
      isChecking,
      checkError,
      isCreating,
      createError,
      createResponse,
      hasCheckedOnce,
      hasAccess,
      normalizedPlanType,
      isBasicPlan,
      isProPlan,
      requiresPaymentMethod,
      statusText,
      checkSubscription,
      createSubscription,
      resetSubscriptionState,
    }),
    [
      subscription,
      isChecking,
      checkError,
      isCreating,
      createError,
      createResponse,
      hasCheckedOnce,
      hasAccess,
      normalizedPlanType,
      isBasicPlan,
      isProPlan,
      requiresPaymentMethod,
      statusText,
      checkSubscription,
      createSubscription,
      resetSubscriptionState,
    ]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return context;
};
